import { Button, Grid, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Link as RRLink,
  Redirect,
  Route,
  Switch,
  useRouteMatch
} from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import { useQueryStringNumber } from '../../../hooks/useQueryString';
import Breadcrumb from '../../Breadcrumb';
import { getProjectAsync } from '../../Project/projectSlice';
import CivilBulkPlanning from './CivilBulkPlanning';
import ElectricalBulkPlanning from './ElectricalBulkPlanning';
import InstrumentBulkPlanning from './InstrumentBulkPlanning';
import MreBulkPlanning from './MreBulkPlanning';
import MseBulkPlanning from './MseBulkPlanning';
import OtherBulkPlanning from './OtherBulkPlanning';
import PipingBulkPlanning from './PipingBulkPlanning';
import PipingReviewDocument from './PipingReviewDocument';
import PobBulkPlanning from './PobBulkPlanning';
import { useTranslation } from 'react-i18next';

export enum DisciplineLabel {
  CVE = 1,
  EE = 2,
  HSE = 3,
  IE = 5,
  MRE = 6,
  PIP = 8,
  ISS = 12,
  ENC = 13,
  POB = 14,
  ETS = 15
}

export enum PurposeLabel {
  IFR = 3,
  IFC = 6,
  IFF = 8
}

export enum RevisionLabel {
  A = 11,
  Zero = 26,
  One = 27
}

export default function BulkPlanning() {
  let { path } = useRouteMatch();

  return (
    <Switch>
      <Route exact path={`${path}/ie`}>
        <InstrumentBulkPlanning />
      </Route>
      <Route exact path={`${path}/mse`}>
        <MseBulkPlanning />
      </Route>
      <Route exact path={`${path}/pip`}>
        <PipingBulkPlanning />
      </Route>
      <Route exact path={`${path}/pip/documents`}>
        <PipingReviewDocument />
      </Route>
      <Route exact path={`${path}/cve`}>
        <CivilBulkPlanning />
      </Route>
      <Route exact path={`${path}/ee`}>
        <ElectricalBulkPlanning />
      </Route>
      <Route exact path={`${path}/mre`}>
        <MreBulkPlanning />
      </Route>
      <Route exact path={`${path}/pob`}>
        <PobBulkPlanning />
      </Route>
      <Route path={`${path}/:discipline`}>
        <OtherBulkPlanning />
      </Route>
      <Route exact path={path}>
        <BulkPlanningPage />
      </Route>
    </Switch>
  );
}

function BulkPlanningPage() {
  const classes = useStyles();
  let { path } = useRouteMatch();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const projectId = useQueryStringNumber({ query: 'projectId' });

  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectAsync(projectId));
    }
  }, []);

  function nonDocumentInDiscipline(disciplineId: number): boolean {
    // HSE and ETS cannot use bulk planning
    return (
      disciplineId === DisciplineLabel.HSE ||
      disciplineId === DisciplineLabel.ETS
    );
  }

  if (!projectId) {
    return <Redirect to="/projects" />;
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="bulkPlanning"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('bulkPlanning.titles.welcome')}
      </Typography>
      <Typography gutterBottom>{t('bulkPlanning.titles.selectDiscipline')}</Typography>
      <Grid container spacing={2}>
        {disciplines
          .filter((discipline) => !nonDocumentInDiscipline(discipline.id))
          .map((discipline, index) => (
            <Grid key={index} item xs={6} sm={4} md={3} lg={2}>
              <Button
                className={classes.button}
                component={RRLink}
                to={`${path}/${discipline.name.toLowerCase()}?projectId=${projectId}`}
                color="primary"
                variant="contained"
                size="large"
                fullWidth
              >
                <Typography variant="caption" noWrap>
                  {discipline.name}
                </Typography>
              </Button>
            </Grid>
          ))}
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    button: {
      '& .MuiTypography-root': {
        fontSize: 28,
        [theme.breakpoints.down('sm')]: {
          fontSize: 20
        }
      }
    }
  })
);
